import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

function useScrollReveal() {
  const ref = useRef();
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    const el = ref.current;
    if (el) {
      el.querySelectorAll('.fade-up').forEach(child => observer.observe(child));
    }
    return () => observer.disconnect();
  }, []);
  return ref;
}

function Landing() {
  const [content, setContent] = useState({});
  const [gallery, setGallery] = useState([]);
  const [logo, setLogo] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [formStatus, setFormStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const revealRef = useScrollReveal();

  useEffect(() => {
    api.get('/content').then(r => setContent(r.data)).catch(() => {});
    api.get('/gallery').then(r => setGallery(r.data)).catch(() => {});
    api.get('/logo').then(r => setLogo(r.data)).catch(() => {});

    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContact = async (e) => {
    e.preventDefault();
    setSending(true);
    setFormStatus(null);
    try {
      await api.post('/contact', contactForm);
      setFormStatus('success');
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setFormStatus('error');
    }
    setSending(false);
  };

  const scrollTo = useCallback((id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const c = (section, key, fallback = '') => content[section]?.[key] || fallback;

  const valuesStr = c('about', 'values', 'Respeto, Responsabilidad, Honestidad, Solidaridad, Excelencia');
  const valuesList = valuesStr.split(',').map(v => v.trim()).filter(Boolean);

  return (
    <div ref={revealRef}>
      {/* Navbar */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-inner">
          <div className="navbar-brand">
            {logo?.exists ? (
              <img src={logo.url} alt="Logo" className="brand-logo" />
            ) : (
              <div className="brand-icon">L</div>
            )}
            <span>Liceo Adolfo León Gómez</span>
          </div>
          <ul className={`navbar-links ${menuOpen ? 'open' : ''}`}>
            <li><a href="#inicio" onClick={() => scrollTo('inicio')}>Inicio</a></li>
            <li><a href="#nosotros" onClick={() => scrollTo('nosotros')}>Nosotros</a></li>
            <li><a href="#niveles" onClick={() => scrollTo('niveles')}>Niveles</a></li>
            <li><a href="#galeria" onClick={() => scrollTo('galeria')}>Galería</a></li>
          </ul>
          <button className="mobile-toggle" onClick={() => setMenuOpen(!menuOpen)}>
            <span className="material-icons">{menuOpen ? 'close' : 'menu'}</span>
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section id="inicio" className="hero">
        <div className="hero-bg"></div>
        <div className="hero-particles">
          <span></span><span></span><span></span><span></span>
          <span></span><span></span><span></span><span></span>
        </div>
        <div className="hero-content">
          {logo?.exists && (
            <img src={logo.url} alt="Escudo" className="hero-logo" />
          )}
          <div className="hero-badge">
            <span className="material-icons" style={{ fontSize: '0.9rem' }}>school</span>
            Primaria & Bachillerato
          </div>
          <h1 className="hero-title">
            {c('hero', 'title', 'Liceo Adolfo León Gómez')}
          </h1>
          <p>{c('hero', 'subtitle', 'Formando líderes con valores para el futuro')}</p>
          <div className="hero-buttons">
            <a href="https://liceoadolfoleongomez.academik.codecolombia.com/" target="_blank" rel="noopener noreferrer" className="hero-cta">
              <span className="material-icons" style={{ fontSize: '1.1rem' }}>school</span>
              AcademiK
            </a>
            <a href="https://www.syscolegios.com/" target="_blank" rel="noopener noreferrer" className="hero-cta-outline">
              <span className="material-icons" style={{ fontSize: '1.1rem' }}>computer</span>
              Syscolegios
            </a>
            <a href="https://miltonochoa.com.co/web/" target="_blank" rel="noopener noreferrer" className="hero-cta-outline">
              <span className="material-icons" style={{ fontSize: '1.1rem' }}>quiz</span>
              Martes de Prueba
            </a>
          </div>
        </div>
        <div className="hero-scroll">
          <span className="material-icons">keyboard_arrow_down</span>
        </div>
      </section>

      {/* About */}
      <section id="nosotros" className="section">
        <div className="container">
          <div className="about-grid">
            <div className="about-text fade-up">
              <div className="overline-left">Nuestra historia</div>
              <h3>{c('about', 'title', 'Sobre Nosotros')}</h3>
              <p>{c('about', 'description', 'Somos una institución educativa comprometida con la excelencia académica y la formación integral de nuestros estudiantes desde preescolar hasta bachillerato.')}</p>
              <div className="values-chips">
                {valuesList.map((v, i) => <span key={i}>{v}</span>)}
              </div>
            </div>
            <div className="about-cards">
              <div className="about-card fade-up delay-1">
                <div className="card-icon"><span className="material-icons">flag</span></div>
                <h4>{c('about', 'mission_title', 'Misión')}</h4>
                <p>{c('about', 'mission', 'Formar integralmente a nuestros estudiantes mediante una educación de calidad.')}</p>
              </div>
              <div className="about-card fade-up delay-2">
                <div className="card-icon"><span className="material-icons">visibility</span></div>
                <h4>{c('about', 'vision_title', 'Visión')}</h4>
                <p>{c('about', 'vision', 'Ser reconocidos como una institución educativa líder.')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="niveles" className="section section-alt">
        <div className="container">
          <div className="section-header fade-up">
            <div className="overline">Formación académica</div>
            <h2>{c('services', 'title', 'Nuestros Niveles Educativos')}</h2>
            <p>Ofrecemos una educación integral en cada etapa del desarrollo de nuestros estudiantes</p>
            <div className="accent-line"></div>
          </div>
          <div className="services-grid">
            {[1, 2, 3].map(i => (
              <div key={i} className={`service-card fade-up delay-${i}`}>
                <div className="service-icon">
                  <span className="material-icons">{c('services', `item${i}_icon`, 'school')}</span>
                </div>
                <h3>{c('services', `item${i}_title`, `Nivel ${i}`)}</h3>
                <p>{c('services', `item${i}_desc`, 'Descripción del nivel educativo.')}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="galeria" className="section">
        <div className="container">
          <div className="section-header fade-up">
            <div className="overline">Vida escolar</div>
            <h2>{c('gallery', 'title', 'Galería')}</h2>
            <p>{c('gallery', 'subtitle', 'Conoce nuestras instalaciones y actividades')}</p>
            <div className="accent-line"></div>
          </div>
          {gallery.length > 0 ? (
            <div className="gallery-grid">
              {gallery.map((img, idx) => (
                <div key={img.id} className={`gallery-item fade-up delay-${(idx % 3) + 1}`} onClick={() => setLightbox(img)}>
                  <img src={`/api/uploads/gallery/thumb_${img.filename}`} alt={img.title} loading="lazy" />
                  <div className="overlay">
                    <h4>{img.title}</h4>
                    {img.description && <p>{img.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="gallery-empty fade-up">
              <span className="material-icons">photo_library</span>
              <p>Próximamente fotos de nuestras instalaciones y actividades</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>
            <span className="material-icons">close</span>
          </button>
          <img src={`/api/uploads/gallery/${lightbox.filename}`} alt={lightbox.title} onClick={e => e.stopPropagation()} />
          <div className="lightbox-info">
            <h4>{lightbox.title}</h4>
            {lightbox.description && <p>{lightbox.description}</p>}
          </div>
        </div>
      )}

      {/* Contact - hidden for now */}

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand-section">
              <h3>Liceo Adolfo León Gómez</h3>
              <p>Formando líderes con valores, pensamiento crítico y compromiso social desde la educación preescolar hasta el bachillerato.</p>
            </div>
            <div className="footer-col">
              <h4>Navegación</h4>
              <ul>
                <li><a href="#inicio" onClick={() => scrollTo('inicio')}>Inicio</a></li>
                <li><a href="#nosotros" onClick={() => scrollTo('nosotros')}>Nosotros</a></li>
                <li><a href="#niveles" onClick={() => scrollTo('niveles')}>Niveles</a></li>
                <li><a href="#galeria" onClick={() => scrollTo('galeria')}>Galería</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Plataformas</h4>
              <ul>
                <li><a href="https://liceoadolfoleongomez.academik.codecolombia.com/" target="_blank" rel="noopener noreferrer">AcademiK</a></li>
                <li><a href="https://www.syscolegios.com/" target="_blank" rel="noopener noreferrer">Syscolegios</a></li>
                <li><a href="https://miltonochoa.com.co/web/" target="_blank" rel="noopener noreferrer">Martes de Prueba</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{c('footer', 'text', '© 2026 Liceo Adolfo León Gómez. Todos los derechos reservados.')}</p>
            <a href="/admin/login">Administración</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
